import React, { useEffect, useState, useMemo } from "react";
import { Modal, Form, InputNumber, Button, Divider, Typography, Space } from "antd";
import ProductSearch from "./ProductSearch"; // uses ProductDescribe internally

const { Text } = Typography;

export interface QuotationProductEditModalProps {
  open: boolean;
  row: any | null;                 // existing quotation row (items[editIndex])
  onCancel: () => void;
  onSave: (vals: {
    companyId: string;
    companyName: string;
    unitName: string;
    qtyPack: number;               // still sent to parent/API, but read-only (from attribute)
    quotationQty: number;
    price: number;
    discount: number;
    gst_percentage: number;
    // derived:
    discountValue: number;
    discountAmount: number;
    gstValue: number;
    gstAmount: number;
    totalPrice: number;
    productName: string;
    productId: number;
    id: number;
    unitId:number;
  }) => void;
}

const round2 = (n: number) => Math.round((Number(n) || 0) * 100) / 100;

function computeDerived(args: {
  price: number;
  discount: number;
  gst_percentage: number;
  quotationQty: number;
}) {
  const price = Number(args.price) || 0;
  const discount = Number(args.discount) || 0;
  const gst_percentage = Number(args.gst_percentage) || 0;
  const quotationQty = Number(args.quotationQty) || 0;

  const discountValue = round2((price * discount) / 100);
  const discountAmount = round2(price - discountValue); // per-unit after discount, before GST
  const gstValue = round2((discountAmount * gst_percentage) / 100);
  const gstAmount = round2(discountAmount + gstValue);  // per-unit after GST
  const totalPrice = round2(gstAmount * quotationQty);

  return {
    price,
    discount,
    gst_percentage,
    quotationQty,
    discountValue,
    discountAmount,
    gstValue,
    gstAmount,
    totalPrice,
  };
}

const QuotationProductEditModal: React.FC<QuotationProductEditModalProps> = ({
  open,
  row,
  onCancel,
  onSave,
}) => {
  const [form] = Form.useForm();
  const [selectedAttr, setSelectedAttr] = useState<any>(null);
  const [selectedProductName, setSelectedProductName] = useState<string | null>(null);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null); // NEW

  // Initialize from existing row when opening
  useEffect(() => {
    if (!open || !row) return;

    const attr = row?.attribute || {};
    setSelectedAttr(attr);
    setSelectedProductName(row?.name ?? null);
    setSelectedProductId(
      row?.attribute?.productId != null ? Number(row.attribute.productId) : null
    ); // NEW

    form.setFieldsValue({
      quotationQty: Number(attr.quotationQty ?? 0),
      price: Number(attr.price ?? 0),
      discount: Number(attr.discount ?? 0),
      gst_percentage: Number(attr.gst_percentage ?? 0),
    });
  }, [open, row, form]);

  // Live preview
  const priceWatch = Form.useWatch("price", form);
  const discountWatch = Form.useWatch("discount", form);
  const gstWatch = Form.useWatch("gst_percentage", form);
  const qQtyWatch = Form.useWatch("quotationQty", form);

  const liveCalc = computeDerived({
    price: Number(priceWatch || 0),
    discount: Number(discountWatch || 0),
    gst_percentage: Number(gstWatch || 0),
    quotationQty: Number(qQtyWatch || 0),
  });

  // When (+) pressed on an attribute in ProductDescribe
  const handleSelectFromDescribe = (picked: { id: string; name: string; attribute: any }) => {
    const attr = picked?.attribute || {};
    setSelectedAttr(attr);
    setSelectedProductName(picked?.name ?? null);
    setSelectedProductId(
      attr?.productId != null ? Number(attr.productId) :
      picked?.id != null ? Number(picked.id) : null
    ); // NEW

    // Pre-fill from picked attribute; qtyPack is read-only now, so not in form
    form.setFieldsValue({
      // keep existing quotationQty unless empty -> default 1
      quotationQty: Number(form.getFieldValue("quotationQty") ?? row?.attribute?.quotationQty ?? 1),
      price: Number(attr.price ?? 0),
      discount: Number(attr.discount ?? 0),
      gst_percentage: Number(attr.gst_percentage ?? 0),
    });
  };

  // Read-only Pack text like "500 ml"
  const packText = useMemo(() => {
    const base = selectedAttr || row?.attribute || {};
    const qty = base.qty != null ? String(base.qty) : "";
    const unit = base.unitName != null ? String(base.unitName) : "";
    return [qty, unit].filter(Boolean).join(" ").trim() || "-";
  }, [selectedAttr, row]);

  // Numeric pack qty for API payload (still required)
  const qtyPackNumeric = useMemo(() => {
    const base = selectedAttr || row?.attribute || {};
    return Number(base.qty ?? 0);
  }, [selectedAttr, row]);

  const handleSave = async () => {
    const vals = await form.validateFields();
    const calc = computeDerived({
      price: Number(vals.price || 0),
      discount: Number(vals.discount || 0),
      gst_percentage: Number(vals.gst_percentage || 0),
      quotationQty: Number(vals.quotationQty || 0),
    });

    const base = selectedAttr || row?.attribute || {};
    const productName = selectedProductName ?? row?.name ?? "";
    const productId = selectedProductId ?? row?.attribute?.productId ?? null; 
    // Keep your existing onSave contract to update UI locally
    onSave({
      companyId: String(base.companyId ?? ""),
      companyName: String(base.companyName ?? ""),
      unitName: String(base.unitName ?? ""),
      qtyPack: qtyPackNumeric, // read-only, from attribute
      quotationQty: calc.quotationQty,
      price: calc.price,
      discount: calc.discount,
      gst_percentage: calc.gst_percentage,
      discountValue: calc.discountValue,
      discountAmount: calc.discountAmount,
      gstValue: calc.gstValue,
      gstAmount: calc.gstAmount,
      totalPrice: calc.totalPrice,
      productName: productName,
      productId: Number(productId ?? 0), // NEW
      id:row?.attribute?.id,
      unitId:base.unitId
    });
  };

  return (
    <Modal
      title="Edit Quotation Product"
      open={open}
      onCancel={onCancel}
      footer={
        <Space>
          <Button onClick={onCancel}>Cancel</Button>
          <Button type="primary" onClick={handleSave}>
            Save
          </Button>
        </Space>
      }
      destroyOnClose
      width={980}
    >
      {/* Search + Describe (Describe handles the (+) pick) */}
      <div style={{ marginBottom: 12 }}>
        <ProductSearch
          // default mode shows ProductDescribe; DO NOT change its logic
          onSelectToOrder={handleSelectFromDescribe}
          orderProducts={[]}
          searchString={row?.name}
        />
      </div>

      <Divider />

      {/* Adjust numeric values without touching existing logic elsewhere */}
      <Form
        form={form}
        layout="vertical"
        name="editQuotationRow"
        initialValues={{ price: 0, discount: 0, gst_percentage: 0, quotationQty: 1 }}
      >
        {/* Read-only Pack Qty row */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 12,
            marginBottom: 4,
            alignItems: "end",
          }}
        >
          <div>
            <Text type="secondary">Pack</Text>
            <div style={{ fontWeight: 500, marginTop: 4 }}>{packText}</div>
          </div>
        </div>

        <Space
          size="middle"
          style={{ width: "100%", display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12 }}
        >
          <Form.Item
            label="Quotation Qty"
            name="quotationQty"
            rules={[{ required: true, message: "Enter quotation qty" }]}
          >
            <InputNumber min={0} step={1} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            label="Price (Rs.)"
            name="price"
            rules={[{ required: true, message: "Enter price" }]}
          >
            <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            label="Discount (%)"
            name="discount"
            rules={[{ required: true, message: "Enter discount percent" }]}
          >
            <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            label="GST (%)"
            name="gst_percentage"
            rules={[{ required: true, message: "Enter GST percent" }]}
          >
            <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
          </Form.Item>
        </Space>

        <Divider style={{ margin: "8px 0 12px" }} />

        <div>
          <Text type="secondary">Live preview</Text>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 6 }}>
            <div>
              <Text>Discount Value: </Text>
              <Text strong>{liveCalc.discountValue.toFixed(2)}</Text>
            </div>
            <div>
              <Text>Discount Amount: </Text>
              <Text strong>{liveCalc.discountAmount.toFixed(2)}</Text>
            </div>
            <div>
              <Text>GST Value: </Text>
              <Text strong>{liveCalc.gstValue.toFixed(2)}</Text>
            </div>
            <div>
              <Text>GST Amount (per unit): </Text>
              <Text strong>{liveCalc.gstAmount.toFixed(2)}</Text>
            </div>
            <div>
              <Text>Total Price: </Text>
              <Text strong>{liveCalc.totalPrice.toFixed(2)}</Text>
            </div>
          </div>
        </div>
      </Form>
    </Modal>
  );
};

export default QuotationProductEditModal;
